import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useToast } from '../components/ui';
import { newKey } from './api';

export interface Paged<T> { items: T[]; page: number; pageSize: number; total: number; totalPages: number }

/** Filters live in the URL, so any filtered view can be bookmarked or shared with a colleague. */
export function useUrlState<T extends Record<string, string>>(defaults: T) {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => {
    const out = { ...defaults } as Record<string, string>;
    for (const k of Object.keys(defaults)) out[k] = params.get(k) ?? defaults[k]!;
    return out as T;
  }, [params, defaults]);
  const set = useCallback((patch: Partial<T>) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(patch)) {
        if (v === undefined || v === '' || v === defaults[k]) next.delete(k);
        else next.set(k, String(v));
      }
      if (!('page' in patch)) next.delete('page'); // any filter change goes back to page 1
      return next;
    }, { replace: true });
  }, [setParams, defaults]);
  return [state, set] as const;
}

/**
 * Mutation with toasts and cache invalidation. One idempotency key per logical submission:
 * a retry after a network error reuses it (server does the action once); success rotates it.
 */
export function useAction<V, R = unknown>(fn: (vars: V, key: string) => Promise<R>, opts: { success?: string | ((r: R) => string); invalidate?: QueryKey[]; onSuccess?: (r: R) => void } = {}) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = useRef(newKey());
  return useMutation({
    mutationFn: (vars: V) => fn(vars, key.current),
    onSuccess: async (r) => {
      key.current = newKey();
      await Promise.all((opts.invalidate ?? []).map((k) => qc.invalidateQueries({ queryKey: k })));
      if (opts.success) toast.success(typeof opts.success === 'function' ? opts.success(r) : opts.success);
      opts.onSuccess?.(r);
    },
    onError: (e) => toast.error(e),
  });
}
